import { useCallback, useEffect, useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  HardDriveDownload, Download, Trash2, Plus, Clock, FileSpreadsheet, ShieldCheck,
  Upload, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { formatDateTimeIt, OPERATORI, CATEGORIE } from "@/lib/format";

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function Backup() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Import Comuni state
  const [selectedFile, setSelectedFile] = useState(null);
  const [operatore, setOperatore] = useState("Commerciale 3");
  const [categoria, setCategoria] = useState("pubblica_amministrazione");
  const [importing, setImporting] = useState(false);
  const [lastImportResult, setLastImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Import Clienti (Gestionale/Luigi format) state
  const [clientsFile, setClientsFile] = useState(null);
  const [clientsOperatore, setClientsOperatore] = useState("Commerciale 1");
  const [importingClients, setImportingClients] = useState(false);
  const [lastClientsImport, setLastClientsImport] = useState(null);
  const clientsFileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/backup/list");
      setFiles(r.data);
    } catch { toast.error("Errore caricamento backup"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createNow = async () => {
    setCreating(true);
    try {
      const r = await api.post("/backup/create");
      toast.success(`Backup creato: ${r.data.filename}`);
      load();
    } catch { toast.error("Errore creazione backup"); }
    finally { setCreating(false); }
  };

  const download = async (filename) => {
    try {
      const r = await api.get(`/backup/download/${encodeURIComponent(filename)}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Errore download backup"); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/backup/${encodeURIComponent(confirmDelete)}`);
      toast.success("Backup eliminato");
      setConfirmDelete(null);
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const onFileChosen = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xlsm)$/i.test(f.name)) {
      toast.error("Serve un file .xlsx");
      e.target.value = "";
      return;
    }
    setSelectedFile(f);
    setLastImportResult(null);
  };

  const doImportComuni = async () => {
    if (!selectedFile) {
      toast.error("Seleziona un file .xlsx");
      return;
    }
    setImporting(true);
    setLastImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      const r = await api.post(
        `/import/comuni/upload?operatore=${encodeURIComponent(operatore)}&categoria=${encodeURIComponent(categoria)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 },
      );
      setLastImportResult(r.data);
      toast.success(
        `Import completato: ${r.data.clients_inserted} clienti, ${r.data.notes_inserted} note, ${r.data.existing_tagged} aggiornati`,
      );
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore import");
    } finally {
      setImporting(false);
    }
  };

  const onClientsFileChosen = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xlsm)$/i.test(f.name)) {
      toast.error("Serve un file .xlsx");
      e.target.value = "";
      return;
    }
    setClientsFile(f);
    setLastClientsImport(null);
  };

  const doImportClients = async () => {
    if (!clientsFile) {
      toast.error("Seleziona un file .xlsx");
      return;
    }
    setImportingClients(true);
    setLastClientsImport(null);
    try {
      const fd = new FormData();
      fd.append("file", clientsFile);
      const r = await api.post(
        `/import/clients/upload?operatore=${encodeURIComponent(clientsOperatore)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 },
      );
      setLastClientsImport(r.data);
      toast.success(
        `Import completato: ${r.data.clients_inserted} clienti, ${r.data.notes_inserted} note, ${r.data.existing_tagged} riassegnati`,
      );
      setClientsFile(null);
      if (clientsFileRef.current) clientsFileRef.current.value = "";
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore import");
    } finally {
      setImportingClients(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-[#F56A1F] font-semibold mb-2">
            Sicurezza Dati
          </div>
          <h1 className="text-4xl font-semibold tracking-tight text-stone-900">Backup</h1>
          <p className="text-stone-500 mt-1 max-w-2xl">
            Un backup completo di clienti, note giornaliere e richiami viene generato automaticamente
            ogni notte. Puoi anche crearne uno manualmente in qualsiasi momento.
          </p>
        </div>
        <Button
          data-testid="backup-create-btn"
          onClick={createNow}
          disabled={creating}
          className="bg-[#F56A1F] hover:bg-[#E05206] text-white gap-2 shadow-sm h-11 px-5">
          <Plus className="w-4 h-4" />
          {creating ? "Creazione in corso…" : "Crea Backup Adesso"}
        </Button>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <Card className="border-stone-200 shadow-sm rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-[#F56A1F]/10 text-[#F56A1F] flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
            <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">
              Backup Automatico
            </div>
          </div>
          <div className="text-2xl font-semibold text-stone-900">02:00</div>
          <div className="text-sm text-stone-500 mt-1">Ogni notte, fuso Europe/Rome</div>
        </Card>

        <Card className="border-stone-200 shadow-sm rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">
              Retention
            </div>
          </div>
          <div className="text-2xl font-semibold text-stone-900">30 backup</div>
          <div className="text-sm text-stone-500 mt-1">
            I più vecchi vengono ruotati automaticamente
          </div>
        </Card>

        <Card className="border-stone-200 shadow-sm rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-stone-100 text-stone-700 flex items-center justify-center">
              <HardDriveDownload className="w-4 h-4" />
            </div>
            <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">
              Backup Salvati
            </div>
          </div>
          <div className="text-2xl font-semibold text-stone-900">{files.length}</div>
          <div className="text-sm text-stone-500 mt-1">
            {files.length === 0 ? "Nessun backup salvato" : "Pronti per il download"}
          </div>
        </Card>
      </div>

      {/* Import Clienti (formato Gestionale) card */}
      <Card className="border-stone-200 shadow-sm rounded-2xl overflow-hidden mb-6">
        <div className="grain-orange bg-stone-900 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-[#F56A1F]" />
            <div className="text-white font-medium">Importa Clienti Excel (Gestionale)</div>
          </div>
          <div className="text-white/60 text-xs">Assegna un intero file a un commerciale specifico</div>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-stone-500">
            Carica un file <span className="font-mono text-xs bg-stone-100 px-1.5 py-0.5 rounded">.xlsx</span> con
            foglio <em>"NUOVO GESTIONALE 2026"</em>. Ogni cliente verrà taggato con l'operatore scelto e le note della
            riga saranno importate come note giornaliere. I clienti già esistenti verranno riassegnati al nuovo operatore.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">
                File Excel
              </div>
              <input
                ref={clientsFileRef}
                type="file"
                accept=".xlsx,.xlsm"
                onChange={onClientsFileChosen}
                data-testid="import-clients-file"
                className="block w-full text-sm text-stone-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#F56A1F]/10 file:text-[#F56A1F] hover:file:bg-[#F56A1F]/20 file:cursor-pointer cursor-pointer border border-stone-200 rounded-lg bg-white h-10"
              />
              {clientsFile && (
                <div className="text-[11px] text-emerald-700 mt-1 truncate" title={clientsFile.name}>
                  ✓ {clientsFile.name} ({(clientsFile.size / 1024).toFixed(0)} KB)
                </div>
              )}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">
                Assegna a operatore
              </div>
              <Select value={clientsOperatore} onValueChange={setClientsOperatore}>
                <SelectTrigger data-testid="import-clients-operatore" className="h-10 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {OPERATORI.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1 flex-wrap">
            <Button
              data-testid="import-clients-submit"
              onClick={doImportClients}
              disabled={importingClients || !clientsFile}
              className="bg-[#F56A1F] hover:bg-[#E05206] text-white gap-2 h-10 px-5"
            >
              <Upload className="w-4 h-4" />
              {importingClients ? "Import in corso…" : "Importa Excel"}
            </Button>

            {lastClientsImport && (
              <div className="text-xs text-stone-600 flex items-center gap-3 flex-wrap">
                <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-800">
                  {lastClientsImport.clients_inserted} clienti nuovi
                </Badge>
                <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-800">
                  {lastClientsImport.notes_inserted} note
                </Badge>
                {lastClientsImport.existing_tagged > 0 && (
                  <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-800">
                    {lastClientsImport.existing_tagged} riassegnati
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Import Comuni card */}
      <Card className="border-stone-200 shadow-sm rounded-2xl overflow-hidden mb-6">
        <div className="grain-orange bg-stone-900 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#F56A1F]" />
            <div className="text-white font-medium">Importa Comuni (Pubblica Amministrazione)</div>
          </div>
          <div className="text-white/60 text-xs">Aggiunge una lista di comuni assegnandoli a un commerciale</div>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-stone-500">
            Carica un file <span className="font-mono text-xs bg-stone-100 px-1.5 py-0.5 rounded">.xlsx</span> con
            le colonne <em>comune · referente · recapito · mail · mail 2 · piattaforma · nota1 · nota2 · nota3 · date</em>.
            I comuni già esistenti verranno riassegnati all'operatore scelto (mantenendo le altre informazioni).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">
                File Excel
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                onChange={onFileChosen}
                data-testid="import-comuni-file"
                className="block w-full text-sm text-stone-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#F56A1F]/10 file:text-[#F56A1F] hover:file:bg-[#F56A1F]/20 file:cursor-pointer cursor-pointer border border-stone-200 rounded-lg bg-white h-10"
              />
              {selectedFile && (
                <div className="text-[11px] text-emerald-700 mt-1 truncate" title={selectedFile.name}>
                  ✓ {selectedFile.name} ({(selectedFile.size / 1024).toFixed(0)} KB)
                </div>
              )}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">
                Assegna a operatore
              </div>
              <Select value={operatore} onValueChange={setOperatore}>
                <SelectTrigger data-testid="import-comuni-operatore" className="h-10 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {OPERATORI.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">
                Categoria
              </div>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger data-testid="import-comuni-categoria" className="h-10 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {CATEGORIE.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Button
              data-testid="import-comuni-submit"
              onClick={doImportComuni}
              disabled={importing || !selectedFile}
              className="bg-[#F56A1F] hover:bg-[#E05206] text-white gap-2 h-10 px-5"
            >
              <Upload className="w-4 h-4" />
              {importing ? "Import in corso…" : "Importa Excel"}
            </Button>

            {lastImportResult && (
              <div className="text-xs text-stone-600 flex items-center gap-3 flex-wrap">
                <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-800">
                  {lastImportResult.clients_inserted} clienti nuovi
                </Badge>
                <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-800">
                  {lastImportResult.notes_inserted} note
                </Badge>
                {lastImportResult.existing_tagged > 0 && (
                  <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-800">
                    {lastImportResult.existing_tagged} esistenti aggiornati
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="border-stone-200 shadow-sm rounded-2xl overflow-hidden">
        <div className="grain-orange bg-[#F56A1F] px-6 py-3 flex items-center justify-between">
          <div className="text-white font-medium">Cronologia Backup</div>
          <div className="text-white/80 text-xs">Formato .xlsx compatibile con Excel</div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-stone-400">Caricamento…</div>
        ) : files.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-stone-100 flex items-center justify-center mb-3">
              <FileSpreadsheet className="w-6 h-6 text-stone-400" />
            </div>
            <div className="text-stone-500 mb-4">Nessun backup ancora creato.</div>
            <Button onClick={createNow} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" /> Crea il primo backup
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {files.map((f) => (
              <div
                key={f.filename}
                data-testid={`backup-row-${f.filename}`}
                className="flex items-center gap-4 px-6 py-4 hover:bg-stone-50/60 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                  <FileSpreadsheet className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-stone-900 truncate">{f.filename}</div>
                  <div className="text-xs text-stone-500 flex items-center gap-3 mt-0.5">
                    <span>{formatDateTimeIt(f.created_at)}</span>
                    <span>·</span>
                    <span>{fmtBytes(f.size)}</span>
                  </div>
                </div>
                <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-800 text-[10px] uppercase tracking-widest">
                  Excel
                </Badge>
                <Button
                  data-testid={`backup-download-${f.filename}`}
                  onClick={() => download(f.filename)}
                  variant="outline"
                  size="sm"
                  className="gap-1.5">
                  <Download className="w-3.5 h-3.5" />
                  Scarica
                </Button>
                <button
                  data-testid={`backup-delete-${f.filename}`}
                  onClick={() => setConfirmDelete(f.filename)}
                  className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Il file <span className="font-mono text-xs">{confirmDelete}</span> verrà eliminato definitivamente dal server.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
